import React, { useCallback, useEffect, useRef, useState } from 'react';
import { confirmDialog } from 'primereact/confirmdialog';
import { RoleService } from '../../layouts/services/role.service';
import { MetasApiError, MetasColaboradorService } from '../services/metasColaborador.service';
import { COMENTARIO_MIN_POR_OMISION } from '../types/metas.types';
import type { EventoHistorial, FilaVista, MiAlcance, TiendaRef, TiendaSemana, VistaTienda } from '../types/metas.types';
import {
  Chequeo,
  chequeosRevision,
  coberturaDeFilas,
  etiquetaDeMotivo,
  eventoDestacado,
  gerenteDe,
  llavesConAlerta,
  metasMuyAltas,
  momentoCapital,
  momentoDeEstado,
  nombreTienda,
  quienEnvioYResolvio,
  textoEstadoLista,
  textoEvento,
  tonoDiferencia,
} from '../utils/autorizacion';
import { CODIGO_META_A_BODEGUERO, esMetaABodeguero } from '../utils/bodeguero';
import { CODIGO_META_A_CASA, esFilaCasa, esMetaACasa } from '../utils/casa';
import { ajusteDelGerente, marcasEnPalabras, pasoPorGerente } from '../utils/captura';
import { GRUPO_CONFIRMAR } from '../utils/confirmar';
import { capturaDeTienda } from '../utils/flujo';
import { momentoMx, nombrePropio, pctConSigno, pesosConSigno, pesosEnteros } from '../utils/formato';
import { indicadorMetaTienda } from '../utils/kpis';
import { esBloqueoCatalogo, pendientesPorFila, separarPendientes } from '../utils/pendientes';
import { AvisosLista } from './AvisosLista';
import { largoComentario, ReabrirControl } from './ReabrirControl';
import { BarraAccionesFija } from './ui/BarraAccionesFija';
import { CLASE_BOTON, CLASE_BOTON_PRIMARIO, CLASE_PANEL, cx } from './ui/clases';
import { EstadoPill } from './ui/Pill';

interface Props {
  lunes: string;
  /** La fila elegida en la lista. `null`: todavía no se eligió ninguna. */
  fila: TiendaSemana | null;
  alcance: MiAlcance;
  /** Disposición del teléfono (artboard `MovilAutorizacion`): tarjetas y pie fijo. */
  movil?: boolean;
  /**
   * Tras autorizar, rechazar o reabrir: la lista y el contador de la pestaña se
   * recargan. Llega SIEMPRE que el servidor decidió, aunque mientras tanto se
   * haya abierto otra tienda (`tienda` dice cuál se resolvió).
   */
  onResuelto: (mensaje: string, tienda: TiendaRef) => void;
  /** Una decisión falló cuando ya se veía otra tienda: el panel no puede pintarla, la pantalla la avisa. */
  onErrorFuera?: (mensaje: string) => void;
  /**
   * La API completó la revisión que el resumen no traía (resumen viejo): la
   * lista recarga para que la fila deje de decir «por revisar».
   */
  onRevisionCompletada?: () => void;
  /** Enlace a la Captura de esa tienda y semana; `null` si la cuenta no tiene esa pestaña. */
  hrefCaptura?: string | null;
  /** «Ver captura completa»: la pantalla decide si navega o abre la de sólo lectura. */
  onVerCaptura: (vista: VistaTienda) => void;
  /**
   * «Seguimiento» de dirección (21-oct-2026): el mismo detalle sin decisiones
   * (ni comentario, ni autorizar, rechazar o reabrir) y con quién envió y quién
   * resolvió. La API ya manda los permisos en falso; esto quita los controles
   * aunque una respuesta dijera otra cosa.
   */
  soloLectura?: boolean;
}

/**
 * Avisos de la API que la revisión automática ya dice en palabras. La meta al
 * bodeguero (23-oct-2026) tiene su chequeo, al final, fuera del plegado.
 */
const CUBIERTOS_POR_REVISION = new Set([
  'META_BAJO_PROMEDIO',
  'META_MUY_ALTA',
  'SUMA_DISTINTA_A_TIENDA',
  CODIGO_META_A_BODEGUERO,
  // La Casa (ronda F, 23-oct-2026): también tiene su chequeo.
  CODIGO_META_A_CASA,
]);
/**
 * Los de la meta de tienda los dice el chequeo de la suma (incompleta, con IVA
 * o sin meta). Repetidos en «Otros avisos», el panel decía «sin meta de tienda»
 * y «No hay meta de tienda cargada» de una meta con 6 de 7 días (21-oct-2026).
 */
const CUBIERTOS_POR_LA_SUMA = new Set(['SIN_META_TIENDA', 'META_CON_IVA']);

/**
 * El nombre del renglón. Las personas, con mayúscula inicial; la Casa, tal como la
 * nombra la captura («Casa TAF SAN COSME», no «Casa Taf San Cosme»; 26-oct-2026).
 */
const nombreDeFila = (f: { key?: string | null; esCasa?: boolean; nombre: string }): string =>
  esFilaCasa(f) ? f.nombre : nombrePropio(f.nombre);

const ICONO_TONO: Record<Chequeo['tono'], { d: string[]; clase: string; sr: string; circulo?: boolean }> = {
  ok: { d: ['M8 12l3 3 5-6'], clase: 'text-metas-exito', sr: 'Correcto:', circulo: true },
  alerta: { d: ['M12 3l10 18H2z', 'M12 10v4M12 17.5h.01'], clase: 'text-metas-aviso-icono', sr: 'Alerta:' },
  info: { d: ['M12 11v5M12 8h.01'], clase: 'text-metas-texto-2', sr: 'Nota:', circulo: true },
};

const IconoChequeo: React.FC<{ tono: Chequeo['tono'] }> = ({ tono }) => {
  const i = ICONO_TONO[tono];
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={cx('mt-px shrink-0', i.clase)}>
      {i.circulo && <circle cx="12" cy="12" r="9" />}
      {i.d.map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
};

const IconoTriangulo = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3l10 18H2z" />
    <path d="M12 10v4M12 17.5h.01" />
  </svg>
);

const IconoCandado: React.FC<{ tamano?: number }> = ({ tamano = 18 }) => (
  <svg width={tamano} height={tamano} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="mt-px shrink-0 text-metas-error">
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

const IconoX = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

const IconoPalomita = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12l5 5 9-10" />
  </svg>
);

const CLASE_TONO_DIF = { baja: 'text-metas-error', alta: 'text-metas-exito', neutra: 'text-metas-texto-2' } as const;

/** Lo que dice la columna «Meta» de una persona sin meta: su motivo, o por qué no la lleva. */
function textoSinMeta(f: FilaVista, alcance: MiAlcance): { texto: string; pendiente: boolean } {
  // La Casa (ronda F) sólo lleva meta, opcional: nunca queda pendiente.
  if (esFilaCasa(f)) return { texto: 'Sin meta (Casa)', pendiente: false };
  if (f.sinMetaMotivo) {
    const etiqueta = etiquetaDeMotivo(f.sinMetaMotivo, [...alcance.motivos, ...(alcance.motivosHistoricos ?? [])]);
    return { texto: f.sinMetaMotivo === 'otro' && f.sinMetaDetalle ? `${etiqueta}: ${f.sinMetaDetalle}` : etiqueta, pendiente: false };
  }
  if (f.reglaMeta === 'no_lleva') return { texto: 'Sin meta (puesto)', pendiente: false };
  if (f.reglaMeta === 'opcional') return { texto: 'Sin meta', pendiente: false };
  return { texto: 'Pendiente', pendiente: true };
}

/**
 * Detalle de UNA tienda para el regional, a la derecha de la lista (escritorio)
 * o a pantalla completa (teléfono). Reemplaza a `RevisionDialog`.
 *
 * Lo que se decide aquí:
 * - **Autorizar tienda**: aunque tenga alertas (una por una sí se puede; el lote
 *   sólo lleva las que no tienen).
 * - **Rechazar tienda**: exige comentario de `comentarioMin` caracteres (10). Si
 *   falta, el error sale EN EL CAMPO y no se manda nada: un botón deshabilitado
 *   no dice por qué.
 * - **Reabrir** (sólo admin, una autorizada).
 * Nadie resuelve lo que él mismo envió: los permisos los da la API. En
 * «Seguimiento» (`soloLectura`, dirección, 21-oct-2026) no se decide nada.
 *
 * Fase B (21-oct-2026): si la capturó el gerente, se dice quién y cuándo, con
 * su nota, y bajo cada meta que el supervisor cambió, «El gerente puso $X»; bajo
 * cada persona, lo que lleva por persona (transporte, bodeguero fijo,
 * incapacidad, apoyo).
 *
 * ⚠️ Sólo se pinta la respuesta del ÚLTIMO pedido (contador `pedido`), y al
 * decidir se comprueba que la vista en pantalla sea de la fila elegida: una
 * respuesta tardía de otra tienda mandaba su `version` a la URL equivocada.
 */
export const PanelDetalleTienda: React.FC<Props> = ({
  lunes,
  fila,
  alcance,
  movil = false,
  onResuelto,
  onErrorFuera,
  onRevisionCompletada,
  hrefCaptura,
  onVerCaptura,
  soloLectura = false,
}) => {
  const minimo = Math.max(1, alcance.parametros.comentarioMin ?? COMENTARIO_MIN_POR_OMISION);
  // La pastilla dice «Con el gerente» sólo si la tienda captura; si no (por omisión,
  // 22-oct-2026, decisión del usuario), «En captura» como antes.
  const capturaTienda = capturaDeTienda(alcance);
  const [vista, setVista] = useState<VistaTienda | null>(null);
  const [historial, setHistorial] = useState<EventoHistorial[] | null>(null);
  const [errorHistorial, setErrorHistorial] = useState(false);
  const [verTodoHistorial, setVerTodoHistorial] = useState(false);
  const [verOtrosAvisos, setVerOtrosAvisos] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<{ mensaje: string; conflicto: boolean } | null>(null);
  const [comentario, setComentario] = useState('');
  const [errorCampo, setErrorCampo] = useState<string | null>(null);
  const [trabajando, setTrabajando] = useState<null | 'autorizar' | 'rechazar'>(null);
  const campo = useRef<HTMLTextAreaElement>(null);
  const errorCampoRef = useRef<HTMLSpanElement>(null);
  const tituloRef = useRef<HTMLHeadingElement>(null);
  // Cada intento de rechazar sin comentario suficiente: se lleva a la vista el
  // campo CON su error (en el teléfono, el pie fijo tapaba la segunda línea).
  const [avisarCampo, setAvisarCampo] = useState(0);
  const onRevisionRef = useRef(onRevisionCompletada);
  onRevisionRef.current = onRevisionCompletada;
  // Por referencia: la respuesta de una decisión llega después de otros renders
  // (otra tienda elegida, otra lista) y tiene que hablar con los de AHORA.
  const onResueltoRef = useRef(onResuelto);
  onResueltoRef.current = onResuelto;
  const onErrorFueraRef = useRef(onErrorFuera);
  onErrorFueraRef.current = onErrorFuera;

  const chain = fila?.chain ?? null;
  const num = fila?.num ?? null;
  const version = fila?.version ?? null;
  // En una referencia: la lista recarga (y cambia) justo después de completar la revisión.
  const filaRef = useRef(fila);
  filaRef.current = fila;
  // La tienda que se ve AHORA (con su semana): una decisión que responde después
  // de cambiar de tienda no toca el panel, pero sí avisa.
  const tiendaVigente = useRef('');
  tiendaVigente.current = `${lunes}|${chain}|${num}`;
  const decision = useRef(0);

  const pedido = useRef(0);
  const cargar = useCallback(async () => {
    const n = ++pedido.current;
    if (chain === null || num === null) {
      setVista(null);
      setHistorial(null);
      setError(null);
      setCargando(false);
      return;
    }
    setCargando(true);
    setError(null);
    setErrorHistorial(false);
    try {
      const [v, h] = await Promise.all([
        MetasColaboradorService.getVistaTienda(lunes, chain, num),
        MetasColaboradorService.historial(lunes, chain, num).catch(() => null),
      ]);
      if (n !== pedido.current) return;
      setVista(v);
      setHistorial(h ?? []);
      setErrorHistorial(h === null);
      // El resumen de la lista era anterior a la revisión o, en una enviada, ya
      // no dice lo mismo que la de ahora, y abrir el detalle la puso al día (sin
      // gastar versión): la lista recarga para que la fila diga lo mismo que el
      // panel y no ofrezca «sin alertas» para el lote una tienda con alerta.
      const f = filaRef.current;
      if (
        v.revision &&
        f?.resumen &&
        (f.alertas === null || f.alertas === undefined || (f.estado === 'enviada' && f.alertas !== v.revision.alertas))
      )
        onRevisionRef.current?.();
    } catch (e) {
      if (n !== pedido.current) return;
      setVista(null);
      setHistorial(null);
      setError({ mensaje: (e as Error).message, conflicto: false });
    } finally {
      if (n === pedido.current) setCargando(false);
    }
  }, [lunes, chain, num]);

  // Otra tienda u otra semana: se limpia todo lo de la anterior. La misma
  // tienda con otra versión (alguien la resolvió o la reenvió): se relee.
  useEffect(() => {
    setComentario('');
    setErrorCampo(null);
    setVerTodoHistorial(false);
    setVerOtrosAvisos(false);
    setVista(null);
    // Una decisión de la tienda anterior que sigue en camino no bloquea ésta.
    setTrabajando(null);
  }, [lunes, chain, num]);
  // Teléfono: al abrir una tienda (o pasar a otra) el foco va a su nombre; si
  // no, se quedaba en <body>, con el botón que lo tenía ya desmontado.
  useEffect(() => {
    if (movil && chain !== null) tituloRef.current?.focus();
  }, [movil, lunes, chain, num]);
  useEffect(() => {
    if (!avisarCampo) return;
    campo.current?.focus({ preventScroll: true });
    const el = errorCampoRef.current ?? campo.current;
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'center' });
  }, [avisarCampo]);
  useEffect(() => {
    void cargar();
  }, [cargar, version]);

  const largo = largoComentario(comentario);
  const comentarioCorto = largo < minimo;

  const resolver = async (accion: 'autorizar' | 'rechazar') => {
    if (!vista || !fila || trabajando) return;
    const { chain: c, num: k } = vista.tienda;
    if (c !== fila.chain || k !== fila.num || vista.semana.lunes !== lunes) {
      setError({ mensaje: 'La revisión en pantalla no es de esta tienda. Recarga antes de decidir.', conflicto: true });
      return;
    }
    if (accion === 'rechazar' && comentarioCorto) {
      setErrorCampo(
        largo === 0
          ? `Escribe qué debe corregir el supervisor (al menos ${minimo} caracteres).`
          : `Escribe al menos ${minimo} caracteres para que el supervisor sepa qué corregir (llevas ${largo}).`,
      );
      setAvisarCampo((x) => x + 1);
      return;
    }
    const semana = vista.semana.lunes;
    const llave = `${semana}|${c}|${k}`;
    const token = ++decision.current;
    const nombre = `${c} ${k} · ${vista.tienda.nombre}`;
    setTrabajando(accion);
    setError(null);
    try {
      const r =
        accion === 'autorizar'
          ? await MetasColaboradorService.autorizar(semana, c, k, {
              version: vista.estado.version,
              comentario: comentario.trim() || null,
            })
          : await MetasColaboradorService.rechazar(semana, c, k, {
              version: vista.estado.version,
              comentario: comentario.trim(),
            });
      // ⚠️ La decisión ya ocurrió en el servidor: se avisa SIEMPRE (aviso, lista y
      // contador), se vea o no esta tienda. Sólo lo del panel depende de eso.
      if (tiendaVigente.current === llave) {
        setComentario('');
        setErrorCampo(null);
        // Hasta que llegue la vista nueva, la de pantalla dice lo que ya pasó: si
        // no, «Autorizar» volvía a habilitarse sobre la versión vieja y un
        // segundo clic daba 409 en la tienda recién autorizada.
        const nuevo = r?.estado ?? (accion === 'autorizar' ? 'autorizada' : 'rechazada');
        setVista((v) =>
          v && v.semana.lunes === semana && v.tienda.chain === c && v.tienda.num === k
            ? { ...v, estado: { ...v.estado, estado: nuevo, version: r?.version ?? v.estado.version } }
            : v,
        );
      }
      onResueltoRef.current(
        accion === 'autorizar' ? `${nombre}: autorizada.` : `${nombre}: rechazada, el supervisor verá tu comentario.`,
        { chain: c, num: k },
      );
    } catch (e) {
      const mensaje = (e as Error).message;
      if (tiendaVigente.current === llave) {
        // Un pendiente de catálogo que bloquea apareció desde que se abrió: se
        // relee la tienda (sus pendientes son EN VIVO) y se dice por qué.
        if (e instanceof MetasApiError && e.status === 422 && esBloqueoCatalogo(e.errores)) {
          void cargar();
          setError({
            mensaje: `No se autorizó: ${e.errores.length} pendiente(s) de catálogo lo impiden. Se corrigen en origen; revísalos en «Revisión automática».`,
            conflicto: false,
          });
        } else setError({ mensaje, conflicto: e instanceof MetasApiError && e.esConflicto });
      } else onErrorFueraRef.current?.(`${nombre}: ${mensaje}`);
    } finally {
      if (decision.current === token) setTrabajando(null);
    }
  };

  if (!fila) {
    return (
      <aside className={cx(CLASE_PANEL, 'px-6 py-10 text-center text-sm text-metas-texto-2')} aria-label="Detalle de la tienda">
        Elige una tienda de la lista para ver su detalle.
      </aside>
    );
  }

  const titulo = nombreTienda(fila);
  const estado = vista?.estado.estado ?? fila.estado;
  const puedeAutorizar = !soloLectura && !!vista && vista.permisos.autorizar && estado === 'enviada';
  // Sólo una ENVIADA se rechaza (decisión del 29-sep); una respuesta vieja no debe ofrecerlo.
  const puedeRechazar = !soloLectura && !!vista && vista.permisos.rechazar && estado === 'enviada';
  const puedeReabrir = !soloLectura && !!vista && !!vista.permisos.reabrir && estado === 'autorizada';
  const decide = puedeAutorizar || puedeRechazar;

  const revisionApi = vista?.revision ?? fila.revision ?? null;
  // «Colaboradores listos» con la definición única (21-oct-2026): con las filas
  // de la vista, sólo las obligatorias y el desglose por puesto. La API cuenta
  // a todas («10 de 10» donde la Captura dice «4 de 4»).
  const revision = revisionApi && vista ? { ...revisionApi, cobertura: coberturaDeFilas(vista.colaboradores) } : revisionApi;
  const metaTiendaVista = vista?.metaTienda ?? fila.metaTienda;
  const muyAltas = vista ? metasMuyAltas(vista.colaboradores, alcance.parametros.factorMax) : [];
  const conAlerta = llavesConAlerta(revision, muyAltas);
  // Pendientes de catálogo EN VIVO (fase 4): los que bloquean apagan «Autorizar»;
  // los demás son alertas informativas para el regional.
  const pendientesCat = vista?.pendientesCatalogo ?? [];
  const { bloquean: pendBloquean, informan: pendInforman } = separarPendientes(pendientesCat);
  const pendPorFila = pendientesPorFila(pendientesCat);
  const bloqueoCatalogo = vista && estado === 'enviada' ? vista.permisos.bloqueoCatalogo ?? null : null;
  // Autorizar deshabilitado CON su porqué: la cuenta resolvería la tienda, pero un pendiente lo impide.
  const autorizarBloqueado = !!bloqueoCatalogo && !puedeAutorizar && puedeRechazar;
  const chequeos = revision
    ? chequeosRevision(revision, {
        motivos: [...alcance.motivos, ...(alcance.motivosHistoricos ?? [])],
        parametros: alcance.parametros,
        muyAltas,
        corto: movil,
        metaTienda: metaTiendaVista,
      })
    : [];

  const supervisor = nombrePropio(vista?.tienda.supervisor ?? fila.supervisor);
  const enviadoGerenteEn = vista ? vista.estado.enviadoGerenteEn ?? null : fila.enviadoGerenteEn ?? null;
  const enviadoGerenteNombre = vista ? vista.estado.enviadoGerenteNombre ?? null : fila.enviadoGerenteNombre ?? null;
  const notaGerente = vista ? vista.estado.notaGerente ?? null : fila.notaGerente ?? null;
  const conGerente = pasoPorGerente({ estado, enviadoGerenteEn });
  const estadoTexto = momentoDeEstado({
    estado,
    numEnvio: vista?.estado.numEnvio ?? fila.numEnvio,
    enviadoEn: vista?.estado.enviadoEn ?? fila.enviadoEn,
    resueltoEn: vista?.estado.resueltoEn ?? fila.resueltoEn,
    actualizadoEn: vista?.estado.actualizadoEn ?? fila.actualizadoEn,
    enviadoGerenteEn,
  });
  // La Casa no es una persona (ronda F): no cuenta en «N colaboradores»; se dice aparte.
  const colaboradores = vista?.colaboradores.filter((f) => !esFilaCasa(f)).length;
  const conCasa = !!vista?.colaboradores.some((f) => esFilaCasa(f));
  const lineaSup = movil
    ? [supervisor, estadoTexto].filter(Boolean).join(' · ')
    : [
        supervisor ? `Supervisor ${supervisor}` : '',
        estadoTexto,
        colaboradores !== undefined ? `${colaboradores} colaborador${colaboradores === 1 ? '' : 'es'}${conCasa ? ' · Casa' : ''}` : '',
      ]
        .filter(Boolean)
        .join(' · ');

  // «Seguimiento»: quién envió y quién resolvió, con cuándo (lo más nuevo de la vista; si no, la fila).
  const quien = soloLectura
    ? quienEnvioYResolvio({
        estado,
        enviadoPor: vista?.estado.enviadoPor ?? fila.enviadoPor,
        enviadoPorNombre: vista?.estado.enviadoPorNombre ?? fila.enviadoPorNombre,
        enviadoEn: vista?.estado.enviadoEn ?? fila.enviadoEn,
        resueltoPor: vista?.estado.resueltoPor ?? fila.resueltoPor,
        resueltoPorNombre: vista?.estado.resueltoPorNombre ?? fila.resueltoPorNombre,
        resueltoEn: vista?.estado.resueltoEn ?? fila.resueltoEn,
        enviadoGerenteEn,
        enviadoGerenteNombre,
      })
    : [];
  const lineaQuien = quien.length > 0 && (
    <span className="text-[13px] text-metas-texto-2" data-testid="seguimiento-detalle">
      {quien.join(' · ')}
    </span>
  );
  // La capturó el gerente (21-oct-2026): quién, cuándo y su nota, y cuántas
  // metas cambió el supervisor. En «Seguimiento» el quién ya va en `lineaQuien`.
  const ajustes = conGerente && vista ? vista.colaboradores.filter((f) => ajusteDelGerente(f, true) !== null).length : 0;
  const partesGerente = conGerente
    ? [
        soloLectura ? null : `Capturó ${gerenteDe(enviadoGerenteNombre)} ${momentoMx(enviadoGerenteEn)}`.trim(),
        vista
          ? ajustes === 0
            ? 'el supervisor no cambió ninguna meta'
            : `${ajustes === 1 ? '1 meta ajustada' : `${ajustes} metas ajustadas`} por el supervisor`
          : null,
        notaGerente ? `nota del gerente: «${notaGerente}»` : null,
      ].filter((x): x is string => !!x)
    : [];
  const textoGerente = partesGerente.join(' · ');
  const lineaGerente = !!textoGerente && (
    <span className="text-[13px] text-metas-texto-2 [overflow-wrap:anywhere]" data-testid="gerente-detalle">
      {textoGerente.charAt(0).toLocaleUpperCase('es-MX') + textoGerente.slice(1)}
    </span>
  );

  /**
   * Bajo el puesto: «Meta a bodeguero» (23-oct-2026) o «Meta a Casa» (ronda F), en
   * ámbar; el porqué, en la revisión.
   */
  const metaABodeguero = (f: FilaVista) =>
    esMetaABodeguero(f) ? (
      <span className="text-xs font-semibold text-metas-aviso" data-testid={`meta-bodeguero-${f.key}`}>
        Meta a bodeguero
      </span>
    ) : esMetaACasa(f) ? (
      <span className="text-xs font-semibold text-metas-aviso" data-testid={`meta-casa-${f.key}`}>
        Meta a Casa
      </span>
    ) : null;

  /** Bajo el nombre de la persona: lo que lleva por persona y su apoyo (21-oct-2026). */
  const porPersona = (f: FilaVista) => {
    const partes = [...marcasEnPalabras(f), f.apoyo ? `Apoyo ${f.apoyo.chain} ${f.apoyo.num} · ${f.apoyo.dias} d` : null].filter(Boolean);
    return partes.length ? (
      <span className="text-xs text-metas-texto-2" data-testid={`por-persona-${f.key}`}>
        {partes.join(' · ')}
      </span>
    ) : null;
  };
  /** «El gerente puso $X» bajo la meta que cambió el supervisor. */
  const ajusteDe = (f: FilaVista) => {
    const texto = ajusteDelGerente(f, conGerente);
    return texto ? (
      <span className="block text-xs font-semibold text-metas-aviso" data-testid={`ajuste-detalle-${f.key}`}>
        {texto}
      </span>
    ) : null;
  };

  // ------------------------------------------------------------ piezas
  const verCaptura = vista && (
    hrefCaptura ? (
      <a
        href={hrefCaptura}
        // 44 px de alto en teléfono o pantalla táctil (medía 129 × 20; auditoría del 22-oct-2026).
        className="metas-foco inline-flex items-center text-[13px] font-semibold text-metas-primario no-underline hover:text-metas-primario-hover max-md:min-h-metas-tactil [@media(pointer:coarse)]:min-h-metas-tactil"
        onClick={(e) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
          e.preventDefault();
          onVerCaptura(vista);
        }}
      >
        Ver captura completa
      </a>
    ) : (
      <button
        type="button"
        className="metas-foco inline-flex items-center text-[13px] font-semibold text-metas-primario hover:text-metas-primario-hover max-md:min-h-metas-tactil [@media(pointer:coarse)]:min-h-metas-tactil"
        onClick={() => onVerCaptura(vista)}
      >
        Ver captura completa
      </button>
    )
  );

  const t = vista?.totales;
  const metaTienda = vista ? (vista.metaTienda.completa ? vista.metaTienda.monto : null) : fila.metaTienda.completa ? fila.metaTienda.monto : null;
  const capturado = t ? t.suma : fila.suma ?? null;
  const difPct = t ? t.diferenciaPct : fila.diferenciaPct ?? null;
  const dif = t ? t.diferencia : fila.diferencia ?? null;
  // Meta de tienda incompleta o con IVA (revisión de la fase B, 21-oct-2026): el
  // monto y la misma nota que el recuadro de la Captura («Sólo 6 de 7 días
  // cargados»), no «— · sin meta de tienda».
  const metaT = indicadorMetaTienda(metaTiendaVista);
  const kpiMeta =
    metaTienda !== null
      ? { valor: pesosEnteros(metaTienda), nota: null, notaCorta: null, tono: '' }
      : metaT.tono === 'aviso'
        ? {
            valor: metaT.valor,
            nota: metaT.nota,
            notaCorta: metaTiendaVista.conIva ? 'con IVA' : `${metaTiendaVista.dias} de 7 días`,
            tono: 'text-metas-aviso-oscuro',
          }
        : { valor: '—', nota: 'sin meta de tienda', notaCorta: null, tono: '' };
  const kpis = [
    { etiqueta: 'Meta tienda', ...kpiMeta },
    { etiqueta: 'Capturado', valor: pesosEnteros(capturado), nota: null, notaCorta: null, tono: '' },
    {
      etiqueta: 'Diferencia',
      valor: pctConSigno(difPct),
      nota: movil || dif === null ? null : pesosConSigno(dif),
      notaCorta: null,
      tono: difPct === null ? '' : CLASE_TONO_DIF[tonoDiferencia(difPct)],
    },
  ];
  const bloqueKpis = movil ? (
    <div className="grid grid-cols-3 gap-2">
      {kpis.map((k) => (
        <div key={k.etiqueta} role="group" aria-label={k.etiqueta} className="flex flex-col gap-0.5 text-sm">
          <span className="text-[11px] font-semibold uppercase tracking-[0.03em] text-metas-texto-2">{k.etiqueta}</span>
          <span className={cx('num text-[15px] font-semibold', k.tono)}>{k.valor}</span>
          {k.notaCorta && <span className="num text-[11px] text-metas-texto-2">{k.notaCorta}</span>}
        </div>
      ))}
    </div>
  ) : (
    <div className="grid grid-cols-3 border-b border-metas-borde-suave">
      {kpis.map((k, i) => (
        <div key={k.etiqueta} role="group" aria-label={k.etiqueta} className={cx('flex min-w-0 flex-col gap-1.5 py-4', i === 0 ? 'px-6' : 'border-l border-metas-borde-suave px-5')}>
          <span className="text-metas-etiqueta uppercase text-metas-texto-2">{k.etiqueta}</span>
          <span className={cx('num text-xl font-semibold', k.tono)}>{k.valor}</span>
          {k.nota && <span className="num text-xs text-metas-texto-2">{k.nota}</span>}
        </div>
      ))}
    </div>
  );

  const bloqueRevision = (
    <section aria-label="Revisión automática" className={movil ? cx(CLASE_PANEL, 'px-4 py-2') : 'flex flex-col px-6 pb-2 pt-4'} data-testid="revision-automatica">
      {!movil && <h3 className="m-0 mb-1 text-[13px] font-semibold uppercase tracking-[0.04em] text-metas-texto-3">Revisión automática</h3>}
      {!revision && (
        <p className="m-0 py-2.5 text-sm text-metas-texto-2">
          {cargando ? 'Revisando…' : fila.estado === 'sin_captura' ? 'Todavía no hay captura que revisar.' : 'Sin revisión todavía.'}
        </p>
      )}
      <ul className="m-0 list-none p-0">
        {chequeos.map((c, i) => (
          <li key={c.id} className={cx('flex items-start gap-3 py-2.5', movil && i > 0 && 'border-t border-metas-borde-suave')} data-chequeo={c.id} data-tono={c.tono}>
            <IconoChequeo tono={c.tono} />
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="text-sm">
                <span className="sr-only">{ICONO_TONO[c.tono].sr} </span>
                {c.titulo}
              </span>
              {c.detalle.map((d) => (
                <span key={d} className="num text-xs text-metas-texto-2">
                  {d}
                </span>
              ))}
            </div>
          </li>
        ))}
        {[...pendBloquean, ...pendInforman].map((p, i) => (
          <li
            key={`cat-${p.tipo}-${p.colaboradorKey ?? 'tienda'}-${i}`}
            className={cx('flex items-start gap-3 py-2.5', movil && (chequeos.length > 0 || i > 0) && 'border-t border-metas-borde-suave')}
            data-chequeo="catalogo"
            data-tono={p.bloquea ? 'bloquea' : 'alerta'}
          >
            {p.bloquea ? <IconoCandado /> : <IconoChequeo tono="alerta" />}
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="text-sm [overflow-wrap:anywhere]">
                <span className={cx('font-semibold', p.bloquea ? 'text-metas-error' : 'text-metas-aviso')}>
                  {p.bloquea ? 'Bloquea la autorización' : 'Alerta de catálogo'}
                </span>
                {' · '}
                {p.etiqueta}: {p.colaboradorKey ? nombrePropio(p.referencia) : p.referencia}
              </span>
              <span className="text-xs text-metas-texto-2 [overflow-wrap:anywhere]">
                {p.detalle} Lo corrige {p.area}
                {p.bloquea ? '.' : '; no impide autorizar.'}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );

  /**
   * ¿Algún pendiente de catálogo de la persona BLOQUEA? Entonces la fila lleva
   * el candado rojo y fondo rojo claro: el triángulo ámbar queda sólo para lo
   * que alerta (lenguaje de la fase 4: candado = bloquea, triángulo = alerta;
   * F4-V8).
   */
  const filaBloquea = (key: string): boolean => (pendPorFila.get(key) ?? []).some((p) => p.bloquea);

  /** Bajo el nombre de la persona: sus pendientes de catálogo (ícono + texto). */
  const marcaPendientes = (key: string) => {
    const ps = pendPorFila.get(key);
    if (!ps?.length) return null;
    return (
      <span className="flex flex-col gap-px" data-testid={`pendiente-fila-${key}`}>
        {ps.map((p) => (
          <span key={p.tipo} className={cx('inline-flex items-center gap-1 text-xs font-semibold', p.bloquea ? 'text-metas-error' : 'text-metas-aviso')} title={p.detalle}>
            {p.bloquea ? <IconoCandado tamano={12} /> : <IconoTriangulo />}
            {p.etiqueta}
            {p.bloquea ? ' · bloquea' : ''}
          </span>
        ))}
      </span>
    );
  };

  const filasColab = vista?.colaboradores ?? [];
  const bloquePersonas = vista && (
    movil ? (
      <section aria-label="Metas capturadas" className={cx(CLASE_PANEL, 'px-4 py-1')}>
        <ul className="m-0 list-none p-0">
          {filasColab.map((f, i) => {
            const sin = f.metaMonto === null ? textoSinMeta(f, alcance) : null;
            const alerta = conAlerta.has(f.key) || !!sin?.pendiente || pendPorFila.has(f.key);
            const bloqueaFila = filaBloquea(f.key);
            const vs = pctConSigno(f.vsPromedio);
            return (
              <li
                key={f.key}
                data-alerta={alerta || undefined}
                data-bloquea={bloqueaFila || undefined}
                className={cx(
                  'flex items-center justify-between gap-3 py-2.5',
                  i > 0 && 'border-t border-metas-neutro-fondo',
                  alerta && '-mx-4 px-4',
                  alerta && (bloqueaFila ? 'bg-metas-error-fondo' : 'bg-metas-fila-pendiente'),
                )}
              >
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="flex min-w-0 items-center gap-1.5 text-sm font-medium">
                    {bloqueaFila ? (
                      <span className="shrink-0 text-metas-error">
                        <IconoCandado tamano={14} />
                        <span className="sr-only">Bloquea: </span>
                      </span>
                    ) : (
                      alerta && <span className="sr-only">Con alerta: </span>
                    )}
                    <span className="[overflow-wrap:anywhere]">{nombreDeFila(f)}</span>
                  </span>
                  <span className="text-xs text-metas-texto-2">{nombrePropio(f.puesto) || '—'}</span>
                  {metaABodeguero(f)}
                  {porPersona(f)}
                  {marcaPendientes(f.key)}
                </div>
                <div className="flex shrink-0 flex-col items-end gap-0.5">
                  <span className="num text-sm font-semibold">{sin ? (sin.pendiente ? 'Pendiente' : 'Sin meta') : pesosEnteros(f.metaMonto)}</span>
                  <span className={cx('text-xs', sin || esFilaCasa(f) ? 'text-metas-texto-2' : CLASE_TONO_DIF[tonoDiferencia(f.vsPromedio)])}>
                    {sin
                      ? sin.pendiente
                        ? 'Sin meta ni motivo'
                        : sin.texto
                      : esFilaCasa(f)
                        ? 'nadie la cobra'
                        : vs === '—'
                          ? 'sin promedio'
                          : `${vs} vs. prom.`}
                  </span>
                  {ajusteDe(f)}
                </div>
              </li>
            );
          })}
        </ul>
      </section>
    ) : (
      <div className="flex flex-col px-6 pb-4 pt-2">
        <table className="w-full border-collapse text-sm" aria-label="Metas capturadas">
          <thead>
            <tr className="border-b border-metas-borde-suave">
              <th scope="col" className="py-2 text-left text-metas-th text-metas-texto-2">Colaborador</th>
              <th scope="col" className="w-[118px] py-2 text-right text-metas-th text-metas-texto-2">Meta</th>
              <th scope="col" className="w-[84px] py-2 text-right text-metas-th text-metas-texto-2">vs. prom.</th>
            </tr>
          </thead>
          <tbody>
            {filasColab.map((f) => {
              const sin = f.metaMonto === null ? textoSinMeta(f, alcance) : null;
              const alerta = conAlerta.has(f.key) || !!sin?.pendiente || pendPorFila.has(f.key);
              const bloqueaFila = filaBloquea(f.key);
              return (
                <tr
                  key={f.key}
                  data-alerta={alerta || undefined}
                  data-bloquea={bloqueaFila || undefined}
                  className={cx('border-b border-metas-neutro-fondo', alerta && (bloqueaFila ? 'bg-metas-error-fondo' : 'bg-metas-fila-pendiente'))}
                >
                  <td className="max-w-0 py-2">
                    <div className="flex min-w-0 flex-col gap-px">
                      <span className="flex min-w-0 items-center gap-1.5 font-medium">
                        {bloqueaFila ? (
                          <span className="shrink-0 text-metas-error">
                            <IconoCandado tamano={14} />
                            <span className="sr-only">Bloquea: </span>
                          </span>
                        ) : (
                          alerta && (
                            <span className="shrink-0 text-metas-aviso-icono">
                              <IconoTriangulo />
                              <span className="sr-only">Con alerta: </span>
                            </span>
                          )
                        )}
                        <span className="[overflow-wrap:anywhere]">{nombreDeFila(f)}</span>
                      </span>
                      <span className="text-xs text-metas-texto-2">{nombrePropio(f.puesto) || '—'}</span>
                      {metaABodeguero(f)}
                      {porPersona(f)}
                      {marcaPendientes(f.key)}
                    </div>
                  </td>
                  <td className={cx('num py-2 text-right', sin && 'text-metas-texto-2', sin?.pendiente && 'font-semibold text-metas-aviso')}>
                    {sin ? sin.texto : pesosEnteros(f.metaMonto)}
                    {ajusteDe(f)}
                  </td>
                  <td className={cx('num py-2 text-right text-[13px] font-semibold', sin ? 'font-normal text-metas-texto-2' : CLASE_TONO_DIF[tonoDiferencia(f.vsPromedio)])}>
                    {sin ? '—' : pctConSigno(f.vsPromedio)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )
  );

  const eventos = historial ?? [];
  const destacado = eventoDestacado(eventos);
  const visiblesHistorial = verTodoHistorial ? eventos : eventos.slice(0, 5);
  const bloqueHistorial = movil ? (
    (destacado || errorHistorial) && (
      <div className="rounded-[10px] bg-metas-historial px-3.5 py-3 text-[13px] leading-[1.45] text-metas-texto-3" data-testid="historial">
        {errorHistorial ? (
          'No se pudo leer el historial.'
        ) : (
          destacado && (
            <>
              <b className="font-semibold">{momentoCapital(destacado.fecha)}</b> · {textoEvento(destacado, true)}
            </>
          )
        )}
      </div>
    )
  ) : (
    <section aria-label="Historial" className="mx-6 flex flex-col gap-1.5 rounded-metas-control bg-metas-campo-off px-3.5 py-3" data-testid="historial">
      <h3 className="m-0 text-xs font-semibold uppercase tracking-[0.04em] text-metas-texto-3">Historial</h3>
      {errorHistorial && <span className="text-[13px] text-metas-texto-2">No se pudo leer el historial.</span>}
      {!errorHistorial && historial && !eventos.length && <span className="text-[13px] text-metas-texto-2">Sin movimientos todavía.</span>}
      <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
        {visiblesHistorial.map((e, i) => (
          <li key={`${e.fecha}-${e.accion}-${i}`} className="text-[13px] text-metas-texto-3 [overflow-wrap:anywhere]">
            <b className="font-semibold">{momentoCapital(e.fecha)}</b> · {textoEvento(e)}
          </li>
        ))}
      </ul>
      {eventos.length > 5 && (
        <button type="button" className="metas-foco self-start text-[13px] font-semibold text-metas-primario" onClick={() => setVerTodoHistorial((v) => !v)}>
          {verTodoHistorial ? 'Ver menos' : `Ver todo el historial (${eventos.length})`}
        </button>
      )}
    </section>
  );

  const idComentario = movil ? 'comentario-regional-movil' : 'comentario-regional';
  const bloqueComentario = decide && (
    <div className={cx('flex flex-col gap-1.5', !movil && 'px-6 pt-4')}>
      <label htmlFor={idComentario} className="text-metas-label text-metas-texto-3">
        {movil ? 'Comentario' : 'Comentario para el supervisor'}{' '}
        <span className="font-normal text-metas-texto-2">(obligatorio al rechazar)</span>
      </label>
      <textarea
        id={idComentario}
        ref={campo}
        rows={3}
        maxLength={1000}
        value={comentario}
        onChange={(e) => {
          setComentario(e.target.value);
          if (errorCampo && largoComentario(e.target.value) >= minimo) setErrorCampo(null);
        }}
        placeholder="Ej. Subir la meta de Iván a su venta promedio."
        aria-invalid={errorCampo ? true : undefined}
        aria-describedby={errorCampo ? `${idComentario}-error` : undefined}
        className={cx(
          'metas-foco resize-none rounded-metas-control border bg-metas-panel px-3 py-2.5 leading-[1.45] text-metas-texto',
          movil ? 'text-base' : 'text-sm',
          errorCampo ? 'border-metas-error' : 'border-metas-borde-control',
        )}
      />
      {errorCampo && (
        <span id={`${idComentario}-error`} ref={errorCampoRef} role="alert" className="text-xs font-semibold text-metas-error">
          {errorCampo}
        </span>
      )}
    </div>
  );

  const botonRechazar = puedeRechazar && (
    <button
      type="button"
      // Con `!`: CLASE_BOTON ya trae `text-metas-texto` y `h-metas-control`, y entre
      // clases de Tailwind gana la que va después en el CSS, no en el atributo.
      className={cx(CLASE_BOTON, 'border-metas-error-borde !text-metas-error', movil ? '!h-12' : '!h-11')}
      onClick={() => void resolver('rechazar')}
      disabled={!!trabajando}
      aria-label={movil ? 'Rechazar tienda' : undefined}
    >
      {trabajando === 'rechazar' ? <i className="pi pi-spin pi-spinner" aria-hidden="true" /> : <IconoX />}
      {movil ? 'Rechazar' : 'Rechazar tienda'}
    </button>
  );
  /**
   * En el teléfono (y la tableta) «Autorizar» pregunta antes, nombrando la tienda
   * (auditoría del 22-oct-2026): el botón queda bajo el pulgar en el pie fijo y un
   * doble toque autorizaba una tienda sin verla. En escritorio, como siempre.
   */
  const pedirAutorizar = () => {
    if (!movil) {
      void resolver('autorizar');
      return;
    }
    confirmDialog({
      group: GRUPO_CONFIRMAR,
      header: `¿Autorizar ${titulo}?`,
      message: `Se autoriza ${titulo} completa, tal como la envió ${
        fila?.enviadoPorNombre ? nombrePropio(fila.enviadoPorNombre) : fila?.enviadoPor || 'el supervisor'
      }.`,
      icon: 'pi pi-check-circle',
      acceptLabel: 'Sí, autorizar',
      rejectLabel: 'Cancelar',
      // Lo seguro bajo el dedo: «Cancelar» recibe el foco.
      defaultFocus: 'reject',
      accept: () => void resolver('autorizar'),
    });
  };

  const botonAutorizar = puedeAutorizar && (
    <button
      type="button"
      className={cx(CLASE_BOTON_PRIMARIO, movil ? '!h-12' : '!h-11')}
      onClick={pedirAutorizar}
      disabled={!!trabajando}
      aria-label={movil ? 'Autorizar tienda' : undefined}
    >
      {trabajando === 'autorizar' ? <i className="pi pi-spin pi-spinner" aria-hidden="true" /> : <IconoPalomita />}
      {movil ? 'Autorizar' : 'Autorizar tienda'}
    </button>
  );

  const idBloqueo = movil ? 'bloqueo-catalogo-movil' : 'bloqueo-catalogo';
  const botonAutorizarBloqueado = autorizarBloqueado && (
    <button
      type="button"
      className={cx(CLASE_BOTON_PRIMARIO, movil ? '!h-12' : '!h-11')}
      disabled
      aria-describedby={idBloqueo}
      aria-label={movil ? 'Autorizar tienda' : undefined}
    >
      <IconoPalomita />
      {movil ? 'Autorizar' : 'Autorizar tienda'}
    </button>
  );
  const avisoBloqueo = autorizarBloqueado && (
    <p id={idBloqueo} className={cx('m-0 flex items-start gap-2 text-sm font-semibold text-metas-error', !movil && 'px-6 pt-4')} data-testid="motivo-no-autorizar">
      <IconoCandado />
      <span>No se puede autorizar: {bloqueoCatalogo}</span>
    </p>
  );

  const miCorreo = RoleService.getUserName();
  // En «Seguimiento» no se explica por qué no se decide: esa cuenta nunca decide.
  const sinPermiso =
    vista && estado === 'enviada' && !decide && !soloLectura ? (
      <p className={cx('m-0 text-sm text-metas-texto-2', !movil && 'px-6 pt-4')} data-testid="sin-permiso-resolver">
        {miCorreo && fila.enviadoPor === miCorreo
          ? 'La enviaste tú: la autoriza o rechaza otro regional o un administrador.'
          : 'No puedes autorizar ni rechazar esta tienda desde tu cuenta: nadie resuelve lo que él mismo envió, y la semana tiene que estar en el plazo de resolución.'}
      </p>
    ) : null;

  const otrosAvisos = vista
    ? [...vista.validacion.errores, ...vista.validacion.advertencias.filter((a) => !CUBIERTOS_POR_REVISION.has(a.codigo) && !(revision && CUBIERTOS_POR_LA_SUMA.has(a.codigo)))]
    : [];

  const avisosApi = vista && (
    <div className={cx('flex flex-col gap-2', !movil && 'px-6 pt-4')}>
      {/* Se envió sin errores; si hoy los hay, la plantilla de BUK trajo a alguien después del envío. */}
      {estado === 'enviada' && vista.validacion.errores.length > 0 && (
        <div role="status" className="rounded-metas-control border border-metas-banner-borde bg-metas-banner px-3 py-2 text-sm text-metas-banner-texto" data-testid="aviso-plantilla-cambio">
          Desde el envío cambió la plantilla: hay {vista.validacion.errores.length} línea(s) por resolver.
          {soloLectura ? '' : ' Considera rechazarla para que el supervisor la complete.'}
        </div>
      )}
      {vista.fuentePlantilla === 'erp' && (
        <div role="status" className="rounded-metas-control border border-metas-banner-borde bg-metas-banner px-3 py-2 text-sm text-metas-banner-texto">
          BUK no respondió: la plantilla salió del ERP y puede traer bajas. Revisa con cuidado o vuelve a intentar más tarde.
        </div>
      )}
      {otrosAvisos.length > 0 && (
        <div>
          <button
            type="button"
            className="metas-foco text-[13px] font-semibold text-metas-primario"
            aria-expanded={verOtrosAvisos}
            onClick={() => setVerOtrosAvisos((v) => !v)}
          >
            {verOtrosAvisos ? 'Ocultar otros avisos' : `Otros avisos de la captura (${otrosAvisos.length})`}
          </button>
          {verOtrosAvisos && (
            <div className="mt-2">
              <AvisosLista avisos={otrosAvisos} />
            </div>
          )}
        </div>
      )}
    </div>
  );

  const bloqueError = error && (
    <div role="alert" className={cx('flex flex-wrap items-center gap-2 rounded-metas-control border border-metas-error bg-metas-error-fondo px-3 py-2 text-sm text-metas-error', !movil && 'mx-6 mt-4')}>
      <span className="font-semibold">{error.mensaje}</span>
      {(error.conflicto || !vista) && (
        <button type="button" className={CLASE_BOTON} onClick={() => void cargar()}>
          {vista ? 'Recargar' : 'Reintentar'}
        </button>
      )}
    </div>
  );

  const reabrir = puedeReabrir && vista && (
    <div className={cx(!movil && 'px-6 pt-4')}>
      <ReabrirControl
        lunes={vista.semana.lunes}
        chain={vista.tienda.chain}
        num={vista.tienda.num}
        version={vista.estado.version}
        comentarioMin={minimo}
        onReabierta={() =>
          onResueltoRef.current(`${vista.tienda.chain} ${vista.tienda.num} · ${vista.tienda.nombre}: reabierta, volvió a borrador.`, {
            chain: vista.tienda.chain,
            num: vista.tienda.num,
          })
        }
        onError={(e) => setError({ mensaje: (e as Error).message, conflicto: e instanceof MetasApiError && e.esConflicto })}
      />
    </div>
  );

  const cargandoTexto = cargando && !vista && <p className="m-0 px-6 py-4 text-sm text-metas-texto-2">Cargando la tienda…</p>;

  // ------------------------------------------------------------ teléfono
  if (movil) {
    return (
      <div className="flex flex-col gap-3" aria-label={`Detalle de ${titulo}`} role="region" aria-busy={cargando || undefined}>
        <section className={cx(CLASE_PANEL, 'flex flex-col gap-3.5 p-4')}>
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between gap-2">
              <h2 ref={tituloRef} tabIndex={-1} className="m-0 text-xl font-semibold focus:outline-none">
                {titulo}
              </h2>
              <EstadoPill estado={estado} texto={textoEstadoLista(estado, soloLectura, capturaTienda)} />
            </div>
            <span className="text-[13px] text-metas-texto-2">{lineaSup}</span>
            {lineaQuien}
            {lineaGerente}
          </div>
          {bloqueKpis}
          {verCaptura && <div>{verCaptura}</div>}
        </section>
        {cargando && !vista && <p className="m-0 text-sm text-metas-texto-2">Cargando la tienda…</p>}
        {bloqueError}
        {(revision || vista) && bloqueRevision}
        {avisosApi}
        {bloquePersonas}
        {bloqueHistorial}
        {sinPermiso}
        {reabrir}
        {bloqueComentario}
        {avisoBloqueo}
        {decide && (
          // Pie fijo del diseño: Rechazar y Autorizar de 48 px, a lo ancho.
          // A lo ancho también en la tableta (la raíz lleva 40 px de relleno desde md).
          <BarraAccionesFija etiqueta="Autorizar o rechazar" className="-mx-4 -mb-6 md:-mx-10">
            {botonRechazar}
            {botonAutorizar || botonAutorizarBloqueado}
          </BarraAccionesFija>
        )}
      </div>
    );
  }

  // ------------------------------------------------------------ escritorio
  return (
    <aside className={cx(CLASE_PANEL, 'flex flex-col pb-6')} aria-label={`Detalle de ${titulo}`} aria-busy={cargando || undefined}>
      <div className="flex flex-col gap-1.5 border-b border-metas-borde-suave px-6 py-5">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="m-0 text-xl font-semibold">{titulo}</h2>
          {/* El mismo nombre que su pastilla en la lista (revisión de la fase B, 21-oct-2026). */}
          <EstadoPill estado={estado} texto={textoEstadoLista(estado, soloLectura, capturaTienda)} />
          <div className="flex-grow" />
          {verCaptura}
        </div>
        <span className="text-[13px] text-metas-texto-2">{lineaSup}</span>
        {lineaQuien}
        {lineaGerente}
      </div>
      {bloqueKpis}
      {cargandoTexto}
      {bloqueError}
      {(revision || vista) && bloqueRevision}
      {avisosApi}
      {bloquePersonas}
      {vista && bloqueHistorial}
      {sinPermiso}
      {reabrir}
      {bloqueComentario}
      {avisoBloqueo}
      {decide && (
        <div className="grid grid-cols-2 gap-3 px-6 pt-4">
          {botonRechazar || <span />}
          {botonAutorizar || botonAutorizarBloqueado || <span />}
        </div>
      )}
    </aside>
  );
};

export default PanelDetalleTienda;
